import React, { useEffect, useState } from 'react';
import moment from 'moment/moment.js';
import {
  Grid,
  Table,
  TableColumnResizing,
  TableHeaderRow,
  TableRowDetail,
  TableSelection,
} from '@devexpress/dx-react-grid-material-ui';
import { DataTypeProvider, IntegratedSelection, RowDetailState, SelectionState } from '@devexpress/dx-react-grid';
import useTable from 'shared/hooks/customHooks/useTable';
import TableWrapper from 'shared/components/tables/TableWrapper';
import {
  CustomClickableFormatter,
  CustomFormatterWithTooltipAndComponent,
  CustomFormatterWithTooltipOnlyIfLongText,
  CustomHeaderCell,
} from 'shared/components/andtComponents/TableComponents/TableComponents';
import { calculateColumnWidths, COST_CENTERS_TABLE_COLUMNS, getColumns } from '../../consts';
import CostCentersNoData from 'users/containers/Organization/components/EmptyStates/CostCentersNoData';
import CostCenterActions from 'users/containers/Organization/CostCenters/CostCenterActions';
import CostCenterAccountsData from 'users/containers/Organization/CostCenters/CostCenterAccountsData';
import {
  getChildRows,
  getCostCenterAccountsCounter,
} from 'users/containers/Organization/CostCenters/costCentersHelperFunctions';
import CreateEditCostCenter from 'users/containers/Organization/CostCenters/costCentersModals/CreateEditCostCenter/CreateEditCostCenter';
import EditCostCenterAccounts from 'users/containers/Organization/CostCenters/costCentersModals/CreateEditCostCenter/EditCostCenterAccounts';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import CostCentersIcon from 'users/containers/Organization/CostCenters/CostCentersIcon';
import { useCostCentersContext } from 'users/containers/Organization/CostCenters/contexts/costCentersContext';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import CostCenterUnassignedNameWrapper from 'users/containers/Organization/CostCenters/CostCentersViews/Unassigned/CostCenterUnassignedNameWrapper';
import UnassignedAccounts from 'users/containers/Organization/CostCenters/CostCentersViews/Unassigned/UnassignedAccounts';
import classnames from 'classnames';

import styles from './../CostCenterTable.module.scss';

const CostCentersTableView = ({
  accountsData,
  costCenters,
  isCreateEditMode,
  isSelectionEnabled,
  noCostCentersInOrg,
}) => {
  const [expandedRowIds, setExpandedRowIds] = useState([]);

  const [localColumnsWidth, setLocalColumnsWidth] = useState();

  useEffect(() => {
    const resizeFunction = () => calculateColumnWidths(setLocalColumnsWidth, Object.values(COST_CENTERS_TABLE_COLUMNS));
    resizeFunction();
    window.addEventListener('resize', resizeFunction);

    // Cleanup listener on component unmount
    return () => window.removeEventListener('resize', resizeFunction);
  }, []);

  const { NewTableRow, CustomToggleCell } = useTable();

  const { isAllExpanded, setIsAllExpanded, isCustomersView, selectedCustomer, setSearch } = useCostCentersContext();

  useEffect(() => {
    if (!isAllExpanded) {
      setExpandedRowIds([]);
    } else {
      setExpandedRowIds(costCenters?.map((row, index) => index));
    }
  }, [isAllExpanded, costCenters]);

  useEffect(() => {
    if (expandedRowIds?.length === 0) {
      setIsAllExpanded(false);
    }
  }, [expandedRowIds, setIsAllExpanded]);

  function isUnassignedRow(row) {
    return row?.id === 'unassigned';
  }

  const CostCenterNameWrapper = (props) => {
    const { row } = props;

    return isUnassignedRow(row) ? (
      <CostCenterUnassignedNameWrapper {...props} />
    ) : (
      <CustomFormatterWithTooltipAndComponent {...props} isLeftComponent>
        <CostCentersIcon />
      </CustomFormatterWithTooltipAndComponent>
    );
  };

  const CostCenterCodeWrapper = (props) => {
    const { row } = props;
    if (isUnassignedRow(row)) {
      return null;
    }
    return <CustomFormatterWithTooltipOnlyIfLongText {...props} isSmallText />;
  };

  const ParentNameWrapper = (props) => {
    const { row } = props;
    let costCenterParent = null;
    if (row?.parentId) {
      costCenterParent = costCenters?.find((cc) => cc.id === row.parentId);
    }

    const onParentCostCenterClick = () => {
      setSearch(costCenterParent?.id);
      setExpandedRowIds([]);
      setIsAllExpanded(false);
    };

    return (
      <CustomClickableFormatter {...props} value={costCenterParent?.name} onClick={() => onParentCostCenterClick()} />
    );
  };

  const NumberOfDirectChildrenWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    const subTreeCostCenters = getChildRows(row, costCenters);
    return (
      <div className="d-flex justify-content-center pe-5">
        <Tooltip title={subTreeCostCenters?.map((cc) => cc.name).join('\n')} placement="top">
          <span>{subTreeCostCenters?.length > 0 ? subTreeCostCenters.length : 0}</span>
        </Tooltip>
      </div>
    );
  };

  const CostCenterAccountsNumberWrapper = (props) => {
    const { row } = props;
    if (isUnassignedRow(row)) {
      return null;
    }
    return (
      <div className="d-flex justify-content-center pe-5">{getCostCenterAccountsCounter(props, accountsData)}</div>
    );
  };

  const CreatedAtWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    return (
      <div className={styles.createdAt}>
        <GenerateIcon iconName={ICONS.calendar.name} />
        Created: {row?.creationTime ? moment(row?.creationTime).format('MMM DD YYYY') : null}
      </div>
    );
  };

  const CostCenterActionsWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    const subTreeCostCenters = getChildRows(row, costCenters);
    return <CostCenterActions costCenter={row} subTreeCostCenters={subTreeCostCenters} />;
  };

  const CreateSubCostCenterWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    return <CreateEditCostCenter parentCcId={row.id} />;
  };

  const CostCenterAccountsDataWrapper = (props) => {
    const { row } = props;
    return isUnassignedRow(row) ? (
      <UnassignedAccounts unassignedAccounts={row.unassignedAccounts} />
    ) : (
      <CostCenterAccountsData {...props} isSelectionEnabled={isSelectionEnabled} isCreateEditMode={isCreateEditMode} />
    );
  };

  const EditCostCenterWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    return <EditCostCenterAccounts costCenter={row} parentCcId={row.id} />;
  };

  const CostCentersNoDataWrapper = (props) => (
    <CostCentersNoData
      {...props}
      noCostCentersInOrg={noCostCentersInOrg}
      mainText={isCustomersView && !selectedCustomer?.name ? 'Customer not selected' : null}
      subTexts={
        isCustomersView && !selectedCustomer?.name ? ['Please select Customer to view their Cost Centers'] : null
      }
    />
  );

  return (
    costCenters && (
      <Grid rows={costCenters} columns={getColumns(COST_CENTERS_TABLE_COLUMNS)} automationid="costCenters-list">
        <SelectionState />
        <IntegratedSelection />
        <RowDetailState expandedRowIds={expandedRowIds} onExpandedRowIdsChange={setExpandedRowIds} />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.COST_CENTER_NAME.columnName]}
          formatterComponent={CostCenterNameWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.COST_CENTER_CODE.columnName]}
          formatterComponent={CostCenterCodeWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.PARENT_NAME.columnName]}
          formatterComponent={ParentNameWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.NUMBER_OF_CHILDREN.columnName]}
          formatterComponent={NumberOfDirectChildrenWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.NUMBER_OF_ACCOUNTS.columnName]}
          formatterComponent={CostCenterAccountsNumberWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.CREATED_AT.columnName]}
          formatterComponent={CreatedAtWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.CREATE_SUB_COST_CENTER.columnName]}
          formatterComponent={CreateSubCostCenterWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.ADD_LINKED_ACCOUNTS.columnName]}
          formatterComponent={EditCostCenterWrapper}
        />
        <DataTypeProvider
          for={[COST_CENTERS_TABLE_COLUMNS.ACTIONS.columnName]}
          formatterComponent={CostCenterActionsWrapper}
        />
        <TableWrapper
          virtual
          noDataCellComponent={CostCentersNoDataWrapper}
          cellComponent={(props) => {
            const { row, column } = props;
            const unassignedRow =
              column.name === COST_CENTERS_TABLE_COLUMNS.COST_CENTER_NAME.columnName ? (
                <Table.Cell {...props} colSpan={getColumns(COST_CENTERS_TABLE_COLUMNS).length}>
                  <CostCenterUnassignedNameWrapper {...props} />
                </Table.Cell>
              ) : null;
            return isUnassignedRow(row) ? unassignedRow : <Table.Cell {...props} />;
          }}
          rowComponent={(props) =>
            isUnassignedRow(props.row) ? (
              <NewTableRow
                expandedRowIds={expandedRowIds}
                setExpandedRowIds={setExpandedRowIds}
                className={classnames(styles.unassignedAccountsRow, {
                  [styles.emptyUnassigned]: !props.row.unassignedAccounts?.length,
                })}
                {...props}
              />
            ) : (
              <NewTableRow expandedRowIds={expandedRowIds} setExpandedRowIds={setExpandedRowIds} {...props} />
            )
          }
          height="auto"
        />
        <TableSelection showSelectionColumn={false} />
        <TableColumnResizing
          resizingMode="nextColumn"
          columnWidths={localColumnsWidth}
          onColumnWidthsChange={setLocalColumnsWidth}
        />
        <TableRowDetail
          contentComponent={CostCenterAccountsDataWrapper}
          toggleCellComponent={(props) => <CustomToggleCell {...props} hideToggleCell={isUnassignedRow(props?.row)} />}
        />
        <TableHeaderRow cellComponent={CustomHeaderCell} />
      </Grid>
    )
  );
};

export default CostCentersTableView;
